import React, { useState } from 'react';
import useSelectionStore from '@store/useSelectionStore';
import useGameStore from '@store/useGameStore';
import useMetadataStore from '@store/useMetadataStore';
import useMapStore from '@store/useMapStore';
import { undeployUnitApi } from '@api/gameApi';
import { handleTileHover, handleTileLeave } from '@services/mapService';
import { ClickDTO } from '@models/ClickDTO';
import { ClickableType } from '@utils/enums';
import { getUnitState, shouldApplyStateColorConversion } from '@utils/unitStates';
import { CONFIG } from '@utils/constants';
import { getAssetPath } from '@utils/brandHelper';
import NetOverlay from './NetOverlay';
import SleepOverlay from './SleepOverlay';
import ShieldOverlay from './ShieldOverlay';
import './UnitIcon.css';
import '@styles/unitStates.css';

/**
 * Convert unit template name to slug format
 * Example: "Swordsman" -> "swordsman", "Heavy Knight" -> "heavy-knight"
 */
const getUnitSlug = (name) => {
  if (!name) return '';
  return name.toLowerCase().replace(/\s+/g, '-');
};

/**
 * Get dynamic unit image path
 * @param {string} slug - Unit slug (e.g., "swordsman")
 * @param {string} size - Icon size ('small', 'big', or 'map')
 * @returns {string} Path to image
 */
const getUnitImagePath = (slug, size) => {
  let imageSize;

  if (size === 'small') {
    imageSize = 'small';
  } else if (size === 'map') {
    imageSize = 'map';
  } else {
    imageSize = 'medium';
  }

  return getAssetPath(`images/units/${slug}_${imageSize}.webp`);
};

/**
 * ✅ CINEMATIC-AWARE - Muestra el ícono de una unidad con estado selected y cinematográfico
 * @param {number} unitId - ID de la unidad (busca en store)
 * @param {object} unitData - Datos directos de la unidad (opcional, para Lobby/Shop)
 * @param {string|number} size - 'small', 'big', or a number in pixels
 * @param {boolean} forceSelected - Forzar estado selected (opcional)
 * @param {boolean} showInitiative - Mostrar badge de orden de iniciativa (solo en mapa)
 * @param {boolean} clickable - Si el icono es clickeable (default: true)
 * @param {boolean} showUndeployButton - Mostrar botón X para undeploy (solo en fase DEPLOY)
 */
function UnitIcon({ unitId, unitData, size = 'small', forceSelected = false, showInitiative = false, clickable = true, showUndeployButton = false }) {
  const { currentClick, handleClick } = useSelectionStore();
  const [imageError, setImageError] = useState(false);

  // Get smart unit data REACTIVELY:
  // - If unitData provided (Lobby/Shop), use it directly
  // - Otherwise, use reactive selector to get unit from current snapshot
  // This ensures the component re-renders when the snapshot changes (e.g., during cinematics)
  const unitFromStore = useGameStore(state => {
    if (unitData || !unitId) return null;
    // Get from current units (deployed)
    const units = state.game?.dynamic_objects?.units || [];
    let unit = units.find(u => u.id === unitId);
    if (unit) return unit;
    // Check available units (not deployed)
    const availableUnits = state.game?.available_units || [];
    return availableUnits.find(u => u.id === unitId) || null;
  });
  const smartUnit = unitData || unitFromStore;

  if (!smartUnit) {
    console.warn('[UnitIcon] No unit data provided. Pass either unitId or unitData prop.');
    return null;
  }

  // Get unit slug and image path
  const unitSlug = getUnitSlug(smartUnit.unit_template?.name);
  const imagePath = getUnitImagePath(unitSlug, size);

  // ✅ Verificar si esta unidad está seleccionada
  const isSelected = forceSelected || (
    currentClick?.type === ClickableType.UNIT &&
    currentClick?.data?.id === smartUnit.id
  );

  // ✅ Check if unit is in AOE preview (will be affected by AOE action)
  const aoePreviewUnits = useMapStore(state => state.visual?.aoePreviewUnits) || [];
  const aoePreviewBeneficial = useMapStore(state => state.visual?.aoePreviewBeneficial) || false;
  const isInAoePreview = aoePreviewUnits.includes(smartUnit.id);

  // ✅ Check if we're in targeting mode (for AOE preview on hover)
  const targetingAction = useSelectionStore(state => state.targetingAction);
  const isInTargetingMode = !!targetingAction;

  // ✅ Check if this unit's shield broke during cinematic (hardcode for visual)
  const brokenShields = useMapStore(state => state.visual?.brokenShields) || [];
  const isShieldBroken = brokenShields.includes(smartUnit.id);

  // Handle hover for AOE preview when in targeting mode
  const handleUnitHover = () => {
    if (isInTargetingMode && smartUnit.x !== undefined && smartUnit.y !== undefined) {
      handleTileHover(smartUnit.x, smartUnit.y);
    }
  };

  const handleUnitLeave = () => {
    if (isInTargetingMode) {
      handleTileLeave();
    }
  };

  // Handle unit click (only if clickable)
  const handleUnitClick = clickable ? (e) => {
    e.stopPropagation(); // ✅ Prevent click from bubbling to HighlightLayer
    const clickDTO = new ClickDTO(
      ClickableType.UNIT,
      smartUnit,
      null,
      {
        position: { x: smartUnit.x, y: smartUnit.y },
        team: smartUnit.team,
        gameId: useGameStore.getState().game?.id
      }
    );
    handleClick(clickDTO);
  } : undefined;

  // Handle undeploy button click
  const handleUndeployClick = async (e) => {
    e.stopPropagation();
    const gameId = useGameStore.getState().game?.id;
    if (!gameId || !smartUnit.id) {
      throw new Error('[UnitIcon] Cannot undeploy: missing gameId or unitId');
    }
    await undeployUnitApi(gameId, smartUnit.id);

    // Re-select unit as DEPLOYABLE to show deploy highlights
    const clickDTO = new ClickDTO(ClickableType.DEPLOYABLE, smartUnit, 'UnitIcon-Undeploy');
    handleClick(clickDTO);
  };

  // ✅ Detectar estado de la unidad usando datos cinematográficos
  const unitState = getUnitState(smartUnit);
  const applyColorConversion = unitState && shouldApplyStateColorConversion(unitState);

  // ✅ Detect active effect overlays based on interaction_tags
  // Hydrate effects to ensure interaction_tags are available from template
  useMetadataStore.getState().hydrateUnitEffects(smartUnit);
  const activeEffects = smartUnit.active_effects || [];
  const hasTauntEffect = activeEffects.some(unitEffect =>
    unitEffect.effect?.interaction_tags?.includes('taunt')
  );
  const hasNetEffect = activeEffects.some(unitEffect =>
    unitEffect.effect?.interaction_tags?.includes('immobilize')
  );
  const hasSleepEffect = activeEffects.some(unitEffect =>
    unitEffect.effect?.interaction_tags?.includes('sleep')
  );
  const hasShieldEffect = !isShieldBroken && activeEffects.some(unitEffect =>
    unitEffect.effect?.interaction_tags?.includes('psi_block') &&
    (unitEffect.variables?.shield_charges ?? 0) > 0
  );

  // Get initiative order from unit data (sent by backend)
  const initiative = smartUnit.initiative;

  // Handle image loading error
  const handleImageError = () => {
    setImageError(true);
  };

  // Determine fallback content
  const iconFallback = smartUnit.unit_template?.visual_data?.icon_fallback;
  const shouldShowImage = !imageError && unitSlug;
  const shouldShowFallback = imageError || !unitSlug;

  // Determine if size is numeric or a preset
  const isNumericSize = typeof size === 'number';
  const isMapSize = size === 'map';
  const sizeClass = isNumericSize ? '' : `size-${size}`;

  // Calculate font size based on unit size
  const fontSize = isNumericSize ? Math.floor(size * 0.5) : undefined;

  // ✅ Construimos las clases CSS dinámicamente incluyendo selected y estado
  const classNames = [
    'unit-icon',
    'unit-container', // ✅ Added for state indicator positioning
    sizeClass, // Only add size class if not numeric
    `team-${smartUnit.teamClass}`, // Always apply team color
    isSelected ? 'selected' : '',
    applyColorConversion ? `unit-state-${unitState}` : '', // ✅ Add state-based styling
    hasTauntEffect ? 'unit-icon-effect-taunt' : '', // ✅ Taunt effect overlay (red tint)
    hasNetEffect ? 'unit-icon-effect-net' : '', // ✅ Net effect overlay (shows net)
    hasSleepEffect ? 'unit-icon-effect-sleep' : '', // ✅ Sleep effect overlay (shows Zzz)
    isInAoePreview ? `in-aoe-preview ${aoePreviewBeneficial ? 'aoe-beneficial' : 'aoe-harmful'}` : '' // ✅ AOE preview highlight
  ].filter(Boolean).join(' ');

  // Build inline styles for numeric size or map size
  const inlineStyles = isNumericSize ? {
    width: `${size}px`,
    height: `${size}px`,
    fontSize: `${fontSize}px`,
    ...(clickable ? {} : { pointerEvents: 'none' })
  } : clickable ? {} : { pointerEvents: 'none' };

  // ✅ Debug logging for state detection
  if (unitState && CONFIG.DEBUG_MODE) {
    /*console.log(`[UnitIcon] Unit ${smartUnit.id} state detected:`, {
      originalHp: unit?.hp,
      currentHp: smartUnit.hp,
      state: unitState,
      showOnMap: showStateOnMap,
      applyColor: applyColorConversion,
      usingSmartUnit: !!smartUnit
    });*/
  }

  return (
    <div
      className={classNames}
      style={inlineStyles}
      onClick={handleUnitClick}
      onMouseEnter={handleUnitHover}
      onMouseLeave={handleUnitLeave}
      data-unit-id={smartUnit.id}
      data-team={smartUnit.teamClass}
      data-selected={isSelected}
      data-unit-state={unitState || 'alive'} // ✅ For debugging and CSS targeting
      data-clickable={clickable}
    >
      {shouldShowImage ? (
        // Try to load dynamic unit image
        <img
          src={imagePath}
          alt={smartUnit.name}
          className="unit-icon-sprite"
          onError={handleImageError}
        />
      ) : iconFallback ? (
        // Show fallback emoji if available
        <span className="unit-icon-letter">
          {iconFallback}
        </span>
      ) : (
        // Final fallback: show initials
        <span className="unit-icon-letter">
          {(smartUnit.unit_template?.name ?? '').slice(0, 2).toUpperCase()}
        </span>
      )}

      {/* Net effect overlay - shows when unit has immobilize/net effect */}
      {hasNetEffect && <NetOverlay />}

      {/* Sleep effect overlay - shows floating Zzz when unit is asleep */}
      {hasSleepEffect && <SleepOverlay />}

      {/* Shield effect overlay - shows dome when unit has Psionic Shield */}
      {hasShieldEffect && <ShieldOverlay />}

      {/* Initiative order badge - only show on map */}
      {showInitiative && initiative && (
        <div className={`initiative-badge team-${smartUnit.teamClass}`}>
          <span className="initiative-number">{initiative}</span>
        </div>
      )}

      {/* Undeploy button - only show during DEPLOY phase for own units */}
      {showUndeployButton && (
        <button
          className="undeploy-button"
          onClick={handleUndeployClick}
          title="Retirar unidad"
        >
          ✕
        </button>
      )}
    </div>
  );
}

export default UnitIcon;

